import React, { useCallback, useEffect, useRef } from 'react';

import { formatInTimeZone } from 'date-fns-tz';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useFormNav } from '@/hooks/useFormNav';

import { FERT_PLAN_OPTIONS } from '../../utils/constants';
import { OpuSearchFormValues } from '../../utils/schema';
import { OpuSearchFormComments } from '../OpuSearchFormComments';

type Props = {
  control: Control<OpuSearchFormValues>;
  watch: UseFormWatch<OpuSearchFormValues>;
  setValue: UseFormSetValue<OpuSearchFormValues>;
  cytoplasmQualityData: any[];
  setTimeNow: () => void;
};

export const OpuSearchFormFields = ({
  control,
  watch,
  setValue,
  cytoplasmQualityData,
  setTimeNow,
}: Props) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const gradeABCRef = React.useRef<HTMLInputElement | null>(null);
  const didFocus = useRef(false);

  const {
    ref: formRef,
    onKeyDownCapture: navOnKeyDownCapture,
    selectOnFocus,
  } = useFormNav<HTMLFormElement>({ sortByTabIndex: true });

  const onEnterByIndex = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();

      const form = formRef.current;
      if (!form) return;

      const cells = Array.from(form.querySelectorAll('.nav-cell'))
        .filter((el) => !el.hasAttribute('aria-disabled'))
        .sort((a, b) => {
          const aIndex = Number(a.getAttribute('data-enter') ?? 0);
          const bIndex = Number(b.getAttribute('data-enter') ?? 0);
          return aIndex - bIndex;
        });
      const current = (e.target as Element).closest('.nav-cell');
      const idx = current ? cells.indexOf(current) : -1;

      const next = cells[(idx + 1) % cells.length] as HTMLElement;
      const focusable = next?.querySelector<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([disabled])',
      );
      focusable?.focus();
      (focusable as HTMLInputElement | HTMLTextAreaElement)?.select?.();
    },
    [formRef],
  );

  const handleSetTimeNow = useCallback(() => {
    const tz = searchParams.get('tz');
    if (tz) {
      try {
        const timeStr = formatInTimeZone(new Date(), tz, 'HHmm');
        setValue('ivmTechTime', timeStr, { shouldDirty: true });
        return;
      } catch (err) {
        // ignore bad tz and fall back
      }
    }
    setTimeNow();
  }, [searchParams, setValue, setTimeNow]);

  const ivmTechTime = watch('ivmTechTime');
  useEffect(() => {
    if (!ivmTechTime) handleSetTimeNow();
    if (!didFocus.current) {
      didFocus.current = true;
      gradeABCRef.current?.focus();
      gradeABCRef.current?.select();
    }
  }, [ivmTechTime, handleSetTimeNow]);

  return (
    <form
      ref={formRef}
      onKeyDownCapture={(e) => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          onEnterByIndex(e);
          return;
        }
        navOnKeyDownCapture(e);
      }}
      style={{ backgroundColor: 'var(--color-background)' }}
      className="flex w-full flex-col gap-2 rounded p-2"
    >
      <div className="grid grid-cols-[8rem_1fr_8rem_1fr] gap-x-3 gap-y-1 text-xs">
        <label className="self-center font-semibold">
          {t('aspiration.ivmTime')}
        </label>
        <div data-enter={1} className="nav-cell flex items-center gap-1">
          <Controller
            name="ivmTechTime"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="number"
                placeholder="hhmm"
                tabIndex={1}
                className="h-7 flex-1 rounded border border-slate-300 px-1 text-center text-xs"
                onFocus={selectOnFocus}
              />
            )}
          />
          <Button
            type="button"
            label={t('common.now')}
            size="small"
            className="h-7 px-2"
            onClick={handleSetTimeNow}
          />
        </div>

        <label className="self-center font-semibold">
          {t('aspiration.fertPlan')}
        </label>
        <div data-enter={7} className="nav-cell">
          <Controller
            name="fertPlan"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                options={FERT_PLAN_OPTIONS}
                placeholder=""
                tabIndex={2}
                className="w-full text-xs"
                onChange={(e) => field.onChange(e.value)}
                pt={{
                  root: { className: 'h-7' },
                  input: { className: 'h-full flex items-center px-1' },
                  trigger: { className: 'h-7 w-6' },
                }}
              />
            )}
          />
        </div>
        <label className="self-center font-semibold">
          {t('grading.gradesABC')}
        </label>
        <div data-enter={2} className="nav-cell">
          <Controller
            name="gradeABC"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                ref={(el: HTMLInputElement | null) => {
                  field.ref(el);
                  gradeABCRef.current = el;
                }}
                type="number"
                step="1"
                tabIndex={3}
                className="h-7 w-full rounded border border-slate-300 px-1 text-center"
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? 0 : Number(e.target.value),
                  )
                }
                onFocus={selectOnFocus}
              />
            )}
          />
        </div>
        <label className="self-center font-semibold">
          {t('aspiration.usdaSeal')}
        </label>
        <div data-enter={8} className="nav-cell">
          <Controller
            name="usdaSeal"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                tabIndex={4}
                className="h-7 w-full rounded border border-slate-300 px-1"
                onFocus={selectOnFocus}
              />
            )}
          />
        </div>
        <label className="self-center font-semibold">
          {t('grading.gradeD')}
        </label>
        <div data-enter={3} className="nav-cell">
          <Controller
            name="gradeD"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="number"
                step="1"
                tabIndex={5}
                className="h-7 w-full rounded border border-slate-300 px-1 text-center"
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? 0 : Number(e.target.value),
                  )
                }
                onFocus={selectOnFocus}
              />
            )}
          />
        </div>
        <label className="self-center font-semibold">
          {t('aspiration.cytoQual')}
        </label>
        <div data-enter={9} className="nav-cell">
          <Controller
            name="ivmCyto"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                options={cytoplasmQualityData}
                placeholder=""
                tabIndex={6}
                className="w-full text-xs"
                pt={{
                  root: { className: 'h-7' },
                  input: { className: 'h-full flex items-center px-1' },
                  trigger: { className: 'h-7 w-6' },
                }}
                dropdownIcon="pi pi-chevron-down text-xs"
                value={field.value}
                onChange={(e) => field.onChange(Number(e.value))}
              />
            )}
          />
        </div>
        <label className="self-center font-semibold">
          {t('grading.gradeE')}
        </label>
        <div data-enter={4} className="nav-cell">
          <Controller
            name="gradeE"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="number"
                step="1"
                tabIndex={7}
                className="h-7 w-full rounded border border-slate-300 px-1 text-center"
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? 0 : Number(e.target.value),
                  )
                }
                onFocus={selectOnFocus}
              />
            )}
          />
        </div>
        <label className="self-center font-semibold">
          {t('aspiration.numInMat')}
        </label>
        <Controller
          name="numInMat"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="number"
              disabled
              tabIndex={8}
              className="h-7 w-full rounded border border-slate-300 bg-slate-50 px-1 text-center"
            />
          )}
        />
        <label className="self-center font-semibold">
          {t('aspiration.dead')}
        </label>
        <div data-enter={5} className="nav-cell">
          <Controller
            name="dead"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="number"
                step="1"
                tabIndex={9}
                className="h-7 w-full rounded border border-slate-300 px-1 text-center"
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? 0 : Number(e.target.value),
                  )
                }
                onFocus={selectOnFocus}
              />
            )}
          />
        </div>
        <label className="self-center font-semibold">
          {t('aspiration.dishType')}
        </label>
        <div data-enter={10} className="nav-cell">
          <Controller
            name="dishType"
            control={control}
            render={({ field }) => (
              <div className="flex h-7 items-center gap-2">
                <label className="flex items-center text-xs">
                  <input
                    type="radio"
                    value="1"
                    checked={field.value === '1'}
                    onChange={() => field.onChange('1')}
                    className="mr-1"
                    tabIndex={10}
                  />
                  {t('opuSearch.round')}
                </label>
                <label className="flex items-center text-xs">
                  <input
                    type="radio"
                    value="2"
                    checked={field.value === '2'}
                    onChange={() => field.onChange('2')}
                    className="mr-1"
                    tabIndex={11}
                  />
                  {t('opuSearch.square')}
                </label>
              </div>
            )}
          />
        </div>
        <label className="self-center font-semibold">{t('common.total')}</label>
        <span className="flex h-7 items-center justify-center rounded border border-slate-300 bg-slate-50 px-2 text-center text-base font-bold">
          {watch('total')}
        </span>
      </div>
      <div data-enter={6} className="nav-cell">
        <OpuSearchFormComments
          control={control}
          comments={watch('comments')}
          setValue={setValue}
        />
      </div>
    </form>
  );
};
